import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Badge } from '@/components/ui/badge'
import { X, Info, ChevronDown } from 'lucide-react'
import { useCreateAssessment } from '../hooks'
import { assessmentsApi } from '../api'
import { useEngineMaturityLevels } from '@/features/maturity-levels/hooks'
import { useOrganizations } from '@/features/organizations/hooks'
import { useEngineDomains } from '@/features/domains/hooks'
import { useToast } from '@/hooks/use-toast'

const createAssessmentSchema = z.object({
  organization_id: z.string().min(1, 'Organization is required'),
  name: z.string().min(1, 'Assessment name is required').max(200, 'Name must be less than 200 characters'),
  description: z.string().optional(),
  status: z.enum(['draft', 'in_progress', 'completed']).default('draft'),
  // New unified scope: e.g. "complete", "level:3", "industry"
  assessment_scope_value: z.string().default('complete'),
  included_domain_ids: z.array(z.string()).optional()
})

type CreateAssessmentFormData = z.infer<typeof createAssessmentSchema>

/** Parse the combined scope select value into separate scope + level fields */
function parseScopeValue(value: string): { assessment_scope: string; scope_level: number | null } {
  if (value.startsWith('level:')) {
    const level = parseInt(value.split(':')[1], 10)
    return { assessment_scope: 'level', scope_level: isNaN(level) ? null : level }
  }
  return { assessment_scope: value, scope_level: null }
}

interface CreateAssessmentFormProps {
  /** When provided, the org selector is hidden and this value is used. */
  organizationId?: string
  onClose: () => void
  onSuccess: (assessmentId?: string) => void
  /** When true, skips status selection, sets status to in_progress, and navigates to questionnaire after creation. */
  autoStart?: boolean
}

export function CreateAssessmentForm({ organizationId, onClose, onSuccess, autoStart = false }: CreateAssessmentFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [domainPopoverOpen, setDomainPopoverOpen] = useState(false)
  const navigate = useNavigate()
  const { toast } = useToast()
  const createAssessmentMutation = useCreateAssessment()

  // Whether the caller already supplied the org — hide the selector if so
  const hasFixedOrg = !!organizationId

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    control,
    formState: { errors }
  } = useForm<CreateAssessmentFormData>({
    resolver: zodResolver(createAssessmentSchema),
    defaultValues: {
      organization_id: organizationId ?? '',
      name: '',
      description: '',
      status: autoStart ? 'in_progress' : 'draft',
      assessment_scope_value: 'complete',
      included_domain_ids: []
    }
  })

  // Fetch maturity levels
  const { data: maturityLevelsData, isLoading: isLoadingMaturityLevels } = useEngineMaturityLevels()
  const maturityLevels = Array.isArray(maturityLevelsData) ? maturityLevelsData : maturityLevelsData?.data || []

  // Fetch organizations list for the selector (only when no fixed org)
  const { data: orgData, isLoading: isLoadingOrgs } = useOrganizations(
    hasFixedOrg ? undefined : { per_page: 100, sort_by: 'name', sort_order: 'asc' }
  )
  const organizations = !hasFixedOrg ? (orgData?.items ?? []) : []

  // Fetch all domains for the multi-select
  const { data: domainsData, isLoading: isLoadingDomains } = useEngineDomains()
  const allDomains: { id: string; name: string; description: string }[] =
    Array.isArray(domainsData) ? domainsData : domainsData?.data ?? domainsData?.items ?? []

  const selectedDomainIds = watch('included_domain_ids') || []
  const scopeValue = watch('assessment_scope_value') || 'complete'

  const toggleDomain = (domainId: string) => {
    const current = selectedDomainIds
    const updated = current.includes(domainId)
      ? current.filter(id => id !== domainId)
      : [...current, domainId]
    setValue('included_domain_ids', updated)
  }

  const selectAllDomains = () => {
    setValue('included_domain_ids', allDomains.map(d => d.id))
  }

  const clearAllDomains = () => {
    setValue('included_domain_ids', [])
  }


  const onSubmit = async (data: CreateAssessmentFormData) => {
    try {
      setIsSubmitting(true)

      // Parse the combined scope value
      const { assessment_scope, scope_level } = parseScopeValue(data.assessment_scope_value)

      const result = await createAssessmentMutation.mutateAsync({
        name: data.name,
        description: data.description,
        organization_id: data.organization_id,
        status: 'draft',
        assessment_scope: assessment_scope as 'complete' | 'level' | 'industry',
        scope_level,
        included_domain_ids: data.included_domain_ids?.length ? data.included_domain_ids : undefined,
      })

      const newId = result?.data?.assessment?.id || result?.data?.id

      if (autoStart && newId) {
        // Call backend to start — enforces one in-progress per org
        try {
          await assessmentsApi.start(newId)
        } catch (startError: any) {
          // Show the problem details error from backend
          const msg = startError.response?.data?.title
            || startError.response?.data?.detail
            || 'Failed to start assessment'
          toast({
            title: "Cannot Start Assessment",
            description: msg,
            variant: "destructive",
          })
          onSuccess(newId)
          onClose()
          return // don't navigate to questionnaire
        }
      }

      toast({
        title: autoStart ? "Assessment Started" : "Assessment Created",
        description: `Assessment "${data.name}" has been ${autoStart ? 'started' : 'created'} successfully.`,
      })

      onSuccess(newId)
      onClose()

      if (autoStart && newId) {
        navigate(`/assessments/${newId}/questionnaire`)
      }
    } catch (error: any) {
      console.error('Failed to create assessment:', error)
      toast({
        title: "Error",
        description: error.response?.data?.title || error.response?.data?.error || "Failed to create assessment. Please try again.",
        variant: "destructive",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  /** Render a human-readable description of the current scope selection */
  const getScopeDescription = () => {
    const parsed = parseScopeValue(scopeValue)
    if (parsed.assessment_scope === 'complete') {
      return 'All questions across all maturity levels will be included.'
    }
    if (parsed.assessment_scope === 'level' && parsed.scope_level) {
      return `Questions up to Level ${parsed.scope_level} will be included per domain. Higher-level questions will be excluded.`
    }
    if (parsed.assessment_scope === 'industry') {
      return 'Questions will be filtered per domain based on your organization\'s industry benchmark targets.'
    }
    return ''
  }

  return (
    <div className="fixed inset-0 !mt-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle>Create New Assessment</CardTitle>
            <CardDescription>
              Create a new IT maturity assessment{hasFixedOrg ? ' for this organization' : ''}
            </CardDescription>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Organization Selector — shown only when organizationId is NOT pre-set */}
            {!hasFixedOrg && (
              <div className="space-y-2">
                <Label htmlFor="organization_id">Organization *</Label>
                <Select
                  value={watch('organization_id') || undefined}
                  onValueChange={(value) => setValue('organization_id', value, { shouldValidate: true })}
                  disabled={isLoadingOrgs}
                >
                  <SelectTrigger className={errors.organization_id ? 'border-red-500' : ''}>
                    <SelectValue placeholder={isLoadingOrgs ? 'Loading...' : 'Select an organization'} />
                  </SelectTrigger>
                  <SelectContent>
                    {organizations.map((org: any) => (
                      <SelectItem key={org.id} value={org.id}>
                        {org.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.organization_id && (
                  <p className="text-sm text-red-500">{errors.organization_id.message}</p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="name">Assessment Name *</Label>
              <Input
                id="name"
                {...register('name')}
                placeholder="e.g., Q4 2024 IT Maturity Assessment"
                className={errors.name ? 'border-red-500' : ''}
              />
              {errors.name && (
                <p className="text-sm text-red-500">{errors.name.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                {...register('description')}
                placeholder="Optional description for this assessment..."
                rows={3}
              />
            </div>

            {/* ── Assessment Scope — unified single-select ─────────────── */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Label>Assessment Scope</Label>
                <Info className="h-4 w-4 text-blue-600" />
              </div>
              <Controller
                control={control}
                name="assessment_scope_value"
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={isLoadingMaturityLevels}
                  >
                    <SelectTrigger id="assessment-scope-select">
                      <SelectValue placeholder={isLoadingMaturityLevels ? "Loading..." : "Select assessment scope"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="complete">
                        Complete Assessment (All Levels)
                      </SelectItem>

                      <SelectSeparator />

                      <SelectGroup>
                        <SelectLabel className="text-xs text-muted-foreground">Target Maturity Level</SelectLabel>
                        {maturityLevels
                          ?.sort((a: any, b: any) => a.level - b.level)
                          .map((level: any) => (
                            <SelectItem key={level.level} value={`level:${level.level}`}>
                              Level {level.level}: {level.name}
                            </SelectItem>
                          ))}
                      </SelectGroup>

                      <SelectSeparator />

                      <SelectItem value="industry">
                        🏢 Industry Benchmark (Based on Industry)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
              <p className="text-xs text-gray-600">
                {getScopeDescription()}
              </p>
            </div>

            {/* ── Included Domains Multi-Select ─────────────────────────── */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Label>Included Domains</Label>
                <Info className="h-4 w-4 text-blue-600" />
              </div>
              <Popover open={domainPopoverOpen} onOpenChange={setDomainPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={domainPopoverOpen}
                    className="w-full justify-between h-auto min-h-[40px] font-normal"
                    disabled={isLoadingDomains}
                    type="button"
                  >
                    <div className="flex flex-wrap gap-1 flex-1 text-left">
                      {isLoadingDomains ? (
                        <span className="text-muted-foreground">Loading domains...</span>
                      ) : selectedDomainIds.length === 0 ? (
                        <span className="text-muted-foreground">All Domains (default)</span>
                      ) : (
                        selectedDomainIds.map(id => {
                          const domain = allDomains.find(d => d.id === id)
                          return domain ? (
                            <Badge key={id} variant="secondary" className="text-xs gap-1">
                              {domain.name}
                              <button
                                type="button"
                                className="ml-0.5 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  toggleDomain(id)
                                }}
                              >
                                <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                              </button>
                            </Badge>
                          ) : null
                        })
                      )}
                    </div>
                    <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                  <div className="p-2 border-b flex justify-between">
                    <button
                      type="button"
                      className="text-xs text-blue-600 hover:underline"
                      onClick={selectAllDomains}
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      className="text-xs text-gray-500 hover:underline"
                      onClick={clearAllDomains}
                    >
                      Clear All
                    </button>
                  </div>
                  <div className="max-h-[200px] overflow-y-auto p-1">
                    {allDomains.map((domain) => {
                      const isSelected = selectedDomainIds.includes(domain.id)
                      return (
                        <label
                          key={domain.id}
                          className="flex items-center gap-2 rounded-md px-2 py-1.5 cursor-pointer hover:bg-gray-50 transition-colors"
                        >
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleDomain(domain.id)}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium">{domain.name}</div>
                            {domain.description && (
                              <div className="text-xs text-gray-500 truncate">{domain.description}</div>
                            )}
                          </div>
                        </label>
                      )
                    })}
                    {allDomains.length === 0 && !isLoadingDomains && (
                      <div className="text-center py-3 text-sm text-gray-500">No domains configured</div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
              <p className="text-xs text-gray-600">
                Select specific domains to include. Leave empty to include all domains.
              </p>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting || isLoadingMaturityLevels || (!hasFixedOrg && isLoadingOrgs)}>
                {isSubmitting ? 'Creating...' : autoStart ? 'Create & Start Assessment' : 'Create Assessment'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
